/**
 * BusinessHistorial — el historial de movimientos de la EMPRESA, en cristiano.
 * Reemplaza la pagina de personas dentro de /business: sin "ON RAMP",
 * sin "RECHARGED_BY_USDC", sin "Unkonw bank" ni IDs crudos a la vista.
 * Visual: .historial-ola + tokens --fx-* (glass Ola A). Solo chrome.
 */
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../../store/global';
import API from '../../lib/api';
import { describirMovimiento, montoMovimiento, estadoMovimiento, fechaHora } from '../../lib/movimientos';

const FILTROS = [
  { k: 'todos', label: 'Todos' },
  { k: 'entradas', label: 'Entradas' },
  { k: 'salidas', label: 'Salidas' },
  { k: 'comisiones', label: 'Comisiones' },
];

function chipTone(chip) {
  if (chip.txt === 'Aprobado') return 'good';
  if (chip.txt === 'Rechazado') return 'bad';
  if (chip.txt === 'En proceso') return 'warn';
  return 'muted';
}

export default function BusinessHistorial() {
  const snap = useSnapshot(state);
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [verMas, setVerMas] = useState(25);

  useEffect(() => {
    if (!snap.user?.id) { setLoading(false); return; }
    API.get(`/transaction/getTransactionsByUser/${snap.user.id}`)
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setMovs(Array.isArray(list) ? list : []);
      })
      .catch(() => setMovs([]))
      .finally(() => setLoading(false));
  }, [snap.user?.id]);

  const filtrados = movs.filter((t) => {
    const { label, detalle } = describirMovimiento(t);
    const { negativo, neutro } = montoMovimiento(t);
    const esComision = String(t.type || '').toUpperCase().includes('COMISION');
    // Una conversion no es entrada ni salida: mueve entre bolsas y no cambia el
    // patrimonio. Contarla en cualquiera de los dos lados descuadra el filtro.
    if (filtro === 'entradas' && (negativo || neutro || esComision)) return false;
    if (filtro === 'salidas' && (!negativo || neutro)) return false;
    if (filtro === 'comisiones' && !esComision) return false;
    if (!q.trim()) return true;
    const txt = `${label} ${detalle} ${t.amount} ${t.currency}`.toLowerCase();
    return txt.includes(q.trim().toLowerCase());
  });

  return (
    <div className="historial-ola">
      <header className="historial-ola__head">
        <p className="historial-ola__eyebrow">Más · Historial</p>
        <h1 className="historial-ola__title">Historial de movimientos</h1>
        <p className="historial-ola__sub">Todo lo que entra y sale de la cuenta de tu empresa, en un solo lugar.</p>
      </header>

      <div className="historial-ola__toolbar">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar movimiento…"
          className="historial-ola__input"
          aria-label="Buscar movimiento"
        />
        <div className="historial-ola__chips" role="tablist" aria-label="Filtros">
          {FILTROS.map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFiltro(f.k)}
              className={`historial-ola__chip${filtro === f.k ? ' on' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="historial-ola__loading">Cargando movimientos…</div>
      ) : filtrados.length === 0 ? (
        <div className="historial-ola__panel historial-ola__empty-panel">
          <p className="historial-ola__empty-title">No hay movimientos que mostrar</p>
          <p className="historial-ola__empty-sub">Aquí verás tus recargas, cobros recibidos, pagos y comisiones.</p>
        </div>
      ) : (
        <>
          <div className="historial-ola__panel historial-ola__scroll">
            <table className="historial-ola__table">
              <thead>
                <tr>
                  {['Fecha', 'Hora', 'Movimiento', 'Monto', 'Estado'].map((n) => (
                    <th key={n} scope="col">{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.slice(0, verMas).map((t, i) => {
                  const { fecha, hora } = fechaHora(t);
                  const { label, detalle } = describirMovimiento(t);
                  const { texto, negativo, neutro } = montoMovimiento(t);
                  const chip = estadoMovimiento(t);
                  const tone = chipTone(chip);
                  const amtCls = neutro
                    ? 'historial-ola__amt historial-ola__amt--muted'
                    : negativo
                      ? 'historial-ola__amt historial-ola__amt--out'
                      : 'historial-ola__amt historial-ola__amt--in';
                  return (
                    <tr key={t._id || i}>
                      <td className="historial-ola__date">{fecha}</td>
                      <td className="historial-ola__time">{hora}</td>
                      <td className="historial-ola__move">
                        <strong>{label}</strong>
                        {detalle && <p>{detalle}</p>}
                      </td>
                      <td className={amtCls}>{texto}</td>
                      <td>
                        <span className={`historial-ola__status historial-ola__status--${tone}`}>
                          <span className="historial-ola__status-dot" aria-hidden="true" />
                          {chip.txt}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtrados.length > verMas && (
            <div className="historial-ola__more">
              <button
                type="button"
                onClick={() => setVerMas(verMas + 25)}
                className="historial-ola__btn historial-ola__btn--ghost"
              >
                Ver más ({filtrados.length - verMas} restantes)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
